import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import { useIntl, useParams } from 'umi';
import moment from 'moment';
import Service from '@/pages/dashboard/components/PageContainer/Service';
import dashboardRequest from '@/pages/dashboard/utils/dashboardRequest';
import {
  TECHLAND_CARD_METRICS_API,
  TECHLAND_GAME_CODE,
  TECHLAND_STUDIO_CODE,
} from '@home/pages/studio/home/_constant';
import {
  applyTrendTimeAdjustments,
  buildTechlandTrendRequest,
  formatTechlandTrendMetricsResponse,
  getTechlandFilterEndForSubtitle,
  getTrendUnitFromPeriod,
} from '@home/pages/studio/home/_trendUtils';
import { LANGUAGE_EN } from '@home/_config/config';
import { getLanguage } from '@home/utils/common';

export interface ITargetLinesConfig {
  api?: string;
  enabled?: boolean;
  /** `YY Plan` 参考线颜色，与 pageConfig 一致 */
  planKpiLineColor?: string;
  /** `YY Budget` 参考线颜色 */
  budgetKpiLineColor?: string;
  /** tooltip 浮窗是否展示 Plan 参考线（默认不展示） */
  planShowInTooltip?: boolean;
  /** tooltip 浮窗是否展示 Budget 参考线（默认不展示） */
  budgetShowInTooltip?: boolean;
  /** tooltip/浮窗小数位格式（默认 twoDecimalBmk00） */
  planLabelFormatter?: string;
  /** tooltip/浮窗小数位格式（默认 twoDecimalBmk00） */
  budgetLabelFormatter?: string;
}

export interface IStudioTrendCardServiceProps {
  api: string;
  dateKey?: string;
  dataKey?: string;
  gameCode?: string;
  metrics?: string[];
  formatData?: (data: any, filterVal?: any) => any;
  formatParams?: (params: Record<string, any>) => Record<string, any>;
  targetLinesConfig?: ITargetLinesConfig;
  startTimeOffset?: number;
  endTimeOffset?: number;
  trendStartTimeFloor?: string | null;
  /** 为 true 时：start_time 固定 2026-01-01，end_time 与 TechlandMultiMetricTrendCard 横轴 7 刻度规则一致 */
  sevenTick2026Mode?: boolean;
  children?: ReactElement | ReactElement[];
  className?: string;
  style?: React.CSSProperties;
  trackData?: string[];
  filterLoading?: boolean;
  data?: Record<string, unknown>;
  filter?: Record<string, unknown>;
  filterVal?: Record<string, unknown>;
  showTimeMap?: Record<string, string>;
  showTimeLoading?: boolean;
}

export default function StudioTrendCardService(props: IStudioTrendCardServiceProps) {
  const {
    api,
    dateKey = 'date',
    dataKey = 'trendCard',
    gameCode = TECHLAND_GAME_CODE,
    metrics = ['ytd_profit', 'ytd_budget', 'ytd_plan'],
    formatData: formatDataProp,
    formatParams: formatParamsProp,
    startTimeOffset,
    endTimeOffset,
    trendStartTimeFloor,
    sevenTick2026Mode,
    targetLinesConfig,
    children,
    className,
    style,
    trackData = ['filterInitData'],
    data: propsData,
    filter: filterFromParent,
    filterVal: filterValFromParent,
    showTimeMap,
    showTimeLoading,
    filterLoading: propsFilterLoading,
  } = props;

  const { locale } = useIntl();
  const isEn = locale === LANGUAGE_EN;

  const { studioId } = useParams() as { studioId?: string; };
  const studioCode = studioId || TECHLAND_STUDIO_CODE;

  const serviceData = useMemo(
    () => ({
      ...(propsData ?? {}),
      filter:
        filterFromParent ??
        filterValFromParent ??
        (propsData as { filter?: Record<string, unknown>; } | undefined)?.filter,
    }),
    [propsData, filterFromParent, filterValFromParent],
  );

  const filterInitData = (propsData as { filterInitData?: unknown; } | undefined)
    ?.filterInitData;

  const beforeRequest = (reqData: any) => {
    const { filterVal: fv, filterInitData, extraParams } = reqData || {};
    const mergedFv = fv ?? filterValFromParent;
    let payload = buildTechlandTrendRequest(
      { filterVal: mergedFv, filterInitData },
      metrics,
    );
    payload = {
      ...payload,
      game_code: gameCode,
      studio_code: studioCode,
    };
    const unit = getTrendUnitFromPeriod(
      (mergedFv as { date_type?: string; })?.date_type ?? payload.period,
    );
    payload = applyTrendTimeAdjustments(payload, {
      unit,
      startTimeOffset,
      endTimeOffset,
      trendStartTimeFloor,
      sevenTick2026Mode,
      filterEndYmd: (mergedFv as { end_time?: string; })?.end_time,
      period: (mergedFv as { date_type?: string; })?.date_type ?? payload.period,
    });
    const next = formatParamsProp
      ? formatParamsProp({ ...payload, ...extraParams })
      : { ...payload, ...extraParams };
    return next;
  };

  const fv = serviceData?.filter as Record<string, any> | undefined;
  const dateType = fv?.date_type ?? 'daily';
  const xDateFormat = dateType === 'monthly' ? 'YYYY-MM' : 'MM-DD';
  const tableDateFormat = dateType === 'monthly' ? 'YYYY-MM' : 'YYYY-MM-DD';
  const tooltipDateFormat = tableDateFormat;

  const shouldFetchTargetLines =
    targetLinesConfig?.enabled !== false && !!studioCode && !!gameCode;
  const targetLinesApi =
    targetLinesConfig?.api ?? TECHLAND_CARD_METRICS_API;

  /** 接口只返回数值；label 随语言在 buildLocalizedTargetLines 中拼接，避免切换中英文重复请求 */
  const [targetLinesRaw, setTargetLinesRaw] = useState<{
    yearShort: string;
    planVal?: number;
    budgetVal?: number;
  } | null>(null);
  const [targetLinesLoading, setTargetLinesLoading] = useState(false);

  const buildLocalizedTargetLines = useMemo(() => {
    if (!targetLinesRaw) {
      return null;
    }
    const { yearShort, planVal, budgetVal } = targetLinesRaw;
    const planLabelFormatter =
      targetLinesConfig?.planLabelFormatter ?? 'twoDecimalBmk00';
    const budgetLabelFormatter =
      targetLinesConfig?.budgetLabelFormatter ?? 'twoDecimalBmk00';
    const lines: Array<{
      label: string;
      value: number;
      color?: string;
      labelFormatter?: string;
      showInTooltip?: boolean;
    }> = [];
    if (planVal !== undefined && planVal !== null) {
      lines.push({
        label: getLanguage(
          isEn,
          `${yearShort} Plan`,
          `${yearShort} 计划`,
        ),
        value: planVal,
        color: targetLinesConfig?.planKpiLineColor,
        labelFormatter: planLabelFormatter,
        showInTooltip: targetLinesConfig?.planShowInTooltip ?? false,
      });
    }
    if (budgetVal !== undefined && budgetVal !== null) {
      lines.push({
        label: getLanguage(
          isEn,
          `${yearShort} Budget`,
          `${yearShort} 预算`,
        ),
        value: budgetVal,
        color: targetLinesConfig?.budgetKpiLineColor,
        labelFormatter: budgetLabelFormatter,
        showInTooltip: targetLinesConfig?.budgetShowInTooltip ?? false,
      });
    }
    if (!lines.length) {
      return null;
    }
    return {
      targetLines: lines,
      planValueForPercent: budgetVal,
    };
  }, [
    targetLinesRaw,
    isEn,
    targetLinesConfig?.planKpiLineColor,
    targetLinesConfig?.budgetKpiLineColor,
    targetLinesConfig?.planShowInTooltip,
    targetLinesConfig?.budgetShowInTooltip,
    targetLinesConfig?.planLabelFormatter,
    targetLinesConfig?.budgetLabelFormatter,
  ]);

  useEffect(() => {
    if (!shouldFetchTargetLines) {
      setTargetLinesRaw(null);
      setTargetLinesLoading(false);
      return;
    }
    setTargetLinesLoading(true);
    const filterEndYmd = getTechlandFilterEndForSubtitle(fv);
    const endM =
      filterEndYmd && moment(filterEndYmd).isValid()
        ? moment(filterEndYmd)
        : moment();
    const yearEnd = endM.clone().endOf('year').format('YYYY-MM-DD');
    const yearShort = endM.format('YY');
    const params = {
      studio_code: studioCode,
      game_code: TECHLAND_GAME_CODE,
      period: 'daily',
      start_time: yearEnd,
      end_time: yearEnd,
      metrics: ['ytd_budget', 'ytd_plan'],
      trend_metrics: [],
      filters: filterInitData ?? {},
      is_confirm: false,
    };
    dashboardRequest({
      url: targetLinesApi,
      method: 'post',
      data: params,
      rawData: true,
    } as any)
      .then((res: any) => {
        const body = res?.data ?? res;
        const list = body?.card_list ?? [];
        const getVal = (name: string) => {
          const item = list.find((i: any) => i?.metrics_name === name);
          const v = item?.value;
          if (v === '' || v === null || v === undefined) {
            return undefined;
          }
          const n = Number(v);
          return Number.isNaN(n) ? undefined : n;
        };
        const planVal = getVal('ytd_plan');
        const budgetVal = getVal('ytd_budget');
        if (
          (planVal === undefined || planVal === null) &&
          (budgetVal === undefined || budgetVal === null)
        ) {
          setTargetLinesRaw(null);
          return;
        }
        setTargetLinesRaw({
          yearShort,
          planVal,
          budgetVal,
        });
      })
      .catch(() => setTargetLinesRaw(null))
      .finally(() => setTargetLinesLoading(false));
  }, [
    shouldFetchTargetLines,
    studioCode,
    gameCode,
    targetLinesApi,
    filterInitData,
    fv?.end_time,
    targetLinesConfig?.planKpiLineColor,
    targetLinesConfig?.budgetKpiLineColor,
    targetLinesConfig?.planShowInTooltip,
    targetLinesConfig?.budgetShowInTooltip,
    targetLinesConfig?.planLabelFormatter,
    targetLinesConfig?.budgetLabelFormatter,
  ]);

  const mergedChildProps = useMemo(() => {
    const base: Record<string, any> = {
      dateKey,
      dataKey,
      dateType,
      xDateFormat,
      dateFormat: tableDateFormat,
      tableDateFormat,
      tooltipDateFormat,
      filterVal: fv,
      targetLinesLoading: shouldFetchTargetLines ? targetLinesLoading : false,
    };
    if (showTimeMap) {
      base.showTimeMap = showTimeMap;
    }
    if (showTimeLoading !== undefined) {
      base.showTimeLoading = showTimeLoading;
    }
    if (sevenTick2026Mode) {
      base.sevenTick2026Mode = true;
    }
    if (buildLocalizedTargetLines?.targetLines?.length) {
      return {
        ...base,
        targetLines: buildLocalizedTargetLines.targetLines,
        planValueForPercent: buildLocalizedTargetLines.planValueForPercent,
      };
    }
    return base;
  }, [
    dateKey,
    dataKey,
    dateType,
    xDateFormat,
    tableDateFormat,
    tooltipDateFormat,
    fv,
    buildLocalizedTargetLines,
    shouldFetchTargetLines,
    targetLinesLoading,
    showTimeMap,
    showTimeLoading,
    sevenTick2026Mode,
  ]);

  const content = (
    <Service
      api={api}
      dataKey={dataKey}
      beforeRequest={beforeRequest}
      formatData={(d: any, filterVal: any) => {
        if (formatDataProp) {
          return formatDataProp(d, filterVal);
        }
        const primary = metrics.find(m => String(m).trim());
        return formatTechlandTrendMetricsResponse(d, {
          filterEndYmd: getTechlandFilterEndForSubtitle(filterVal),
          period: (filterVal as { date_type?: string } | undefined)?.date_type,
          primaryMetricKeys: primary ? [String(primary)] : [],
        });
      }}
      rawData
      trackData={trackData}
      data={{ ...serviceData, extraParams: {} }}
      filterLoading={propsFilterLoading}
    >
      {children &&
        React.Children.map(children, (child: ReactElement) =>
          React.cloneElement(child, {
            ...child.props,
            ...mergedChildProps,
          }),
        )}
    </Service>
  );

  return (
    <div className={className} style={style}>
      {content}
    </div>
  );
}
